/* @flow */
import * as React from 'react';

import AvatarWithTextItem from 'components/AvatarWithTextItem';
import AvatarWithTextItems from 'components/AvatarWithTextItems';
import Coin from 'components/Coin';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import Text from 'components/Text';
import Tooltip from 'components/Tooltip';
import UserAvatar from 'users/components/UserAvatar';
import UserLink from 'users/components/UserLink';

import type { ReachabilityInfo } from 'components/ReachabilityBadge';
import type { ContactabilityStatus } from 'queries/__generated__/globalGQLTypes';

type Props = {
  familySummary: Object,
  maxProfilePics: number,
  orientation: 'horizontal' | 'vertical',
  staticProfilePicture?: boolean,
  onFamilyMemberClick?: Function,
};

export default class GroupMemberFamilyList extends React.Component<Props> {
  static defaultProps = {
    maxProfilePics: 5,
    orientation: 'horizontal',
  };

  renderProfilePicture = (familyMember: {
    id: number,
    name: string,
    uuid: string,
    initials: string,
    profilePictureUrl?: ?string,
    contactabilityStatus?: ContactabilityStatus,
    reachabilityInfo?: ReachabilityInfo,
  }) => {
    const { staticProfilePicture, onFamilyMemberClick } = this.props;

    const profilePicture = <UserAvatar user={familyMember} size="medium" />;

    if (staticProfilePicture) {
      return (
        <LayerTrigger
          key={familyMember.id}
          layer={<Tooltip>{familyMember.name}</Tooltip>}
          placement="bottom"
          alignment="middle">
          <Link
            onClick={event => {
              event.stopPropagation();
              if (onFamilyMemberClick) {
                onFamilyMemberClick(familyMember);
              }
            }}>
            {profilePicture}
          </Link>
        </LayerTrigger>
      );
    }

    return (
      <UserLink
        userId={familyMember.id}
        key={familyMember.id}
        placement="bottom"
        alignment="left"
        block="inline">
        {profilePicture}
      </UserLink>
    );
  };

  renderVerticalFamilyMembersGroup = (
    familyMembers: *[],
    familyMembersGroupTitle: string
  ): Array<*> => {
    const { onFamilyMemberClick } = this.props;

    return familyMembers.map(item => (
      <AvatarWithTextItem
        key={item.id}
        title={item.name}
        avatar={<UserAvatar user={item} />}
        subtext={familyMembersGroupTitle}
        subtle
        onClick={() => onFamilyMemberClick && onFamilyMemberClick(item, familyMembersGroupTitle)}
      />
    ));
  };

  render = () => {
    const { orientation, maxProfilePics } = this.props;

    const familyMembers = this.props.familySummary;
    const jointFamilyMembers = familyMembers.parents.concat(familyMembers.children);
    const tooManyProfilePictures = jointFamilyMembers.length > maxProfilePics;
    const numProfilePictureToRender = tooManyProfilePictures ? maxProfilePics - 1 : maxProfilePics;

    if (orientation === 'vertical') {
      return (
        <div className="GroupMemberFamilyList">
          <AvatarWithTextItems>
            {this.renderVerticalFamilyMembersGroup(familyMembers.parents, 'Parent')}
            {this.renderVerticalFamilyMembersGroup(familyMembers.children, 'Child')}
          </AvatarWithTextItems>
        </div>
      );
    }

    return (
      <React.Fragment>
        {jointFamilyMembers.slice(0, numProfilePictureToRender).map(this.renderProfilePicture)}
        {tooManyProfilePictures && (
          <Coin color="white" border="brand" size="xxxlarge">
            <Text color="brand" size="small">
              +{jointFamilyMembers.length - maxProfilePics + 1}
            </Text>
          </Coin>
        )}
      </React.Fragment>
    );
  };
}
